/**
 * Helper functions for map operations
 */

import { slopeCategoryColor } from '../../../../utils/slopeCategoryColor';

/**
 * Extract coordinates from a slope or location geometry
 * @param {Object} location - Location object with geometry property
 * @returns {Object|null} - Coordinates {lat, lng} or null if extraction failed
 */
export const extractCoordinatesFromGeometry = (location) => {
    if (!location) return null;
  
    // Handle direct lat/long coordinates (custom locations)
    if (location.lat !== undefined && location.long !== undefined) {
      return {
        lat: location.lat,
        lng: location.long
      };
    }
  
    // For locations with no geometry
    if (!location.geometry) return null;
  
    try {
      // Parse geometry if it's a string
      const geom = typeof location.geometry === 'string' 
        ? JSON.parse(location.geometry) 
        : location.geometry;
  
      // Get coordinates based on geometry type
      if (geom.type === 'Point') {
        return {
          lng: geom.coordinates[0],
          lat: geom.coordinates[1],
        };
      } else if (geom.type === 'LineString' && geom.coordinates?.length > 0) {
        // Use the middle point of the line
        const midIndex = Math.floor(geom.coordinates.length / 2);
        return {
          lng: geom.coordinates[midIndex][0],
          lat: geom.coordinates[midIndex][1],
        };
      } else if (
        geom.type === 'Polygon' &&
        geom.coordinates?.length > 0 &&
        geom.coordinates[0]?.length > 0
      ) {
        // Get center of polygon (approximate)
        let sumLng = 0,
          sumLat = 0;
        const points = geom.coordinates[0];
  
        points.forEach(point => {
          sumLng += point[0];
          sumLat += point[1];
        });
  
        return {
          lng: sumLng / points.length,
          lat: sumLat / points.length,
        };
      } else if (geom.type === 'MultiPolygon' && geom.coordinates?.length > 0) {
        // For MultiPolygon, use the first polygon's center
        if (
          geom.coordinates[0]?.length > 0 &&
          geom.coordinates[0][0]?.length > 0
        ) {
          let sumLng = 0,
            sumLat = 0;
          const points = geom.coordinates[0][0]; // First polygon, exterior ring
  
          points.forEach(point => {
            sumLng += point[0];
            sumLat += point[1];
          });
  
          return {
            lng: sumLng / points.length,
            lat: sumLat / points.length,
          };
        }
      }
  
      console.warn('Unsupported geometry type:', geom.type);
      return null;
    } catch (err) {
      console.error('Error extracting coordinates from geometry:', err);
      return null;
    }
  };
  
  /**
   * Create GeoJSON features from slope data
   * @param {Array} slopes - Array of slope objects
   * @param {Object|null} selectedSlope - Currently selected slope
   * @returns {Array} - Array of GeoJSON features
   */
  export const createSlopeFeatures = (slopes, selectedSlope) => {
    if (!slopes || !Array.isArray(slopes)) return [];
    
    return slopes
      .filter(slope => slope.geometry)
      .map(slope => {
        try {
          const geom = typeof slope.geometry === 'string'
            ? JSON.parse(slope.geometry)
            : slope.geometry;
  
          return {
            type: 'Feature',
            geometry: geom,
            properties: {
              id: slope.id,
              name: slope.nom || slope.name,
              difficulty: slope.type_de_piste,
              color: slopeCategoryColor(slope),
              sector: slope.sector,
              selected: selectedSlope?.id === slope.id,
            },
          };
        } catch (err) {
          console.error('Error parsing slope geometry:', err);
          return null;
        }
      })
      .filter(Boolean);
  };